/*
 * Colours for swatches rendered in the browser (Searchspring search results, Swym wishlist,
 * Tagalys), which cannot read the stitch collection metafield that Liquid-rendered cards use.
 * Watches the DOM for .color-swatch elements that carry a swatch-color-<handle> class but no
 * inline colour, fetches just those handles from /search?view=swatch-json&q=... and injects one
 * CSS rule per handle. Results are cached per handle for the session.
 */
(function () {
  const ENDPOINT = '/search?view=swatch-json&q=';
  const CACHE_KEY = 'meshki-swatches-v2';
  const BATCH_SIZE = 40;
  const known = readCache();
  const requested = new Set(Object.keys(known));
  let queue = new Set();
  let timer = null;

  function readCache() {
    try {
      return JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeCache() {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(known));
    } catch (error) {
      /* storage unavailable; the lookup simply repeats on the next page */
    }
  }

  function escapeHandle(handle) {
    return window.CSS && CSS.escape ? CSS.escape(handle) : handle.replace(/[^a-z0-9_-]/gi, '\\$&');
  }

  function inject(map) {
    const rules = [];
    for (const handle in map) {
      const def = map[handle];
      let rule = '';
      if (def.c) rule += `--color-swatch:${def.c};--color-swatch-border:${def.b || def.c};`;
      if (def.i) rule += `--color-swatch-image:url(${def.i});`;
      if (rule) rules.push(`.swatch-color-${escapeHandle(handle)}{${rule}}`);
    }
    if (!rules.length) return;
    const style = document.createElement('style');
    style.setAttribute('data-swatch-styles', '');
    style.textContent = rules.join('\n');
    document.head.appendChild(style);
  }

  function handleOf(element) {
    for (const cls of element.classList) {
      if (cls.startsWith('swatch-color-') && cls.length > 13) return cls.slice(13);
    }
    return null;
  }

  function hasInlineColour(element) {
    return element.style.getPropertyValue('--color-swatch') || element.style.getPropertyValue('--color-swatch-image');
  }

  function collect(root) {
    const nodes = root.querySelectorAll ? root.querySelectorAll('.color-swatch') : [];
    if (root.matches && root.matches('.color-swatch')) enqueue(root);
    nodes.forEach(enqueue);
  }

  function enqueue(element) {
    if (hasInlineColour(element)) return;
    const handle = handleOf(element);
    if (!handle || requested.has(handle)) return;
    queue.add(handle);
    if (!timer) timer = setTimeout(flush, 50);
  }

  async function flush() {
    timer = null;
    const handles = Array.from(queue);
    queue = new Set();
    handles.forEach((handle) => requested.add(handle));
    for (let i = 0; i < handles.length; i += BATCH_SIZE) {
      const batch = handles.slice(i, i + BATCH_SIZE);
      try {
        const response = await fetch(ENDPOINT + encodeURIComponent(batch.join(' ')), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`swatch-json ${response.status}`);
        const data = await response.json();
        Object.assign(known, data.swatches);
        inject(data.swatches);
        writeCache();
      } catch (error) {
        batch.forEach((handle) => requested.delete(handle));
        console.warn('[swatches] lookup failed', error);
      }
    }
  }

  inject(known);
  collect(document);
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) collect(node);
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
